import { ChangeEvent, useEffect, useState } from 'react';
import Link from 'next/link';
import { Box, Typography } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useRouter } from 'next/router';

import theme from 'src/style-system/theme';
import { useSendActivationLinkMutation } from 'src/http/sendActivationLink';

import { validateEmail } from '@utils/validator';
import InputError from '@components/InputError';
import { Form } from '@components/UseForm';
import Controls from '@components/Controls';
import CuminButton from '@components/Button';
import { AppRoutes } from '@utils/appRoutes';
import TCCLogo from '@public/assets/svg/TCCLogo.svg';

import { useStyles } from './useStyles';
import { snackbarInfo } from 'store/utils';
import PageLoaderModal from '@components/PageLoaderModal';

const ActivateAccount = () => {
  const [errors, setErrors] = useState<string>('');
  const [input, setInput] = useState<string>('');
  const [activationLinkSend, setActivationLinkSend] = useState<boolean>(false);

  const classes = useStyles();

  const router = useRouter();

  const resetForm = () => {
    setInput('');
    setErrors('');
  };

  const { sendActivationLink, sendActivationLinkLoading } =
    useSendActivationLinkMutation();

  const validateForm: () => boolean = () => {
    let valid = true;

    const emailValidation = validateEmail(input.trim());

    if (!emailValidation.valid) {
      valid = false;
      setErrors(emailValidation?.messageKey || '');
    }
    return valid;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (errors) {
      setErrors('');
    }
    setInput(value);
  };

  const handleSignIn = async () => {
    if (validateForm()) {
      // send activation link mutation here
      try {
        const res = await sendActivationLink({
          variables: {
            input: {
              email: input.trim(),
            },
          },
        });
        if (res?.data?.sendActivationLink) {
          setActivationLinkSend(true);
          resetForm();
        }
      } catch (error: any) {
        snackbarInfo({
          message: error.message,
          type: 'error',
        });
      }
    }
  };

  //to start reset password in on hit of Enter from keyboard
  useEffect(() => {
    const listener = (event: any) => {
      if (event.code === 'Enter') {
        handleSignIn();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [handleSignIn]);

  const onClickErrorCloseIcon = (name: string) => {
    setErrors('');
  };

  const onClickBackArrow = () => {
    router.back();
  };

  return (
    <>
      {sendActivationLinkLoading ? <PageLoaderModal /> : null}
      <Box className={classes.mainContainer}>
        <Box className={classes.leftSectionContainer} />
        <Box
          bgcolor="whitePepper.main"
          className={classes.rightSectionContainer}
        >
          <Box className={classes.headerContainer}>
            <ArrowBackIosIcon
              fontSize="large"
              sx={{
                color: 'cinnamon.main',
                cursor: 'pointer',
              }}
              onClick={onClickBackArrow}
            />

            <Link href={AppRoutes.HOME_ROUTE}>
              <a>
                <TCCLogo />
              </a>
            </Link>
          </Box>

          {activationLinkSend ? (
            <Box className={classes.sendLinkInstruction}>
              <Typography
                variant="body1"
                align="center"
                color="cumin.main"
                maxWidth="450px"
              >
                Your activation link will have been sent to your inbox! Please
                follow the instructions provided to activate your account.
              </Typography>
            </Box>
          ) : (
            <Box className={classes.wrapper}>
              <Typography
                variant="h2"
                align="center"
                color="cinnamon.main"
                className={classes.activateTitle}
              >
                Activate Account
              </Typography>
              <Typography variant="body2" align="center" color="cumin.main">
                Please enter the email address you used for your subscription
                and follow the link provided.
              </Typography>
              <Box className={classes.formContainer}>
                <Form onSubmit={handleSignIn}>
                  <Box className={classes.formItem}>
                    <Typography
                      variant="body1"
                      align="left"
                      color="cumin.main"
                      ml={{ xs: 3, md: 0 }}
                      mb={{ xs: 1, md: 0 }}
                    >
                      Email
                    </Typography>
                    <Box sx={{ position: 'relative' }}>
                      <Controls.InputField
                        name="email"
                        placeholder="Email Address"
                        value={input}
                        onChange={handleInputChange}
                        className={classes.input}
                      />
                      <InputError
                        error={errors}
                        className={classes.emailError}
                        onClickErrorCloseIcon={() =>
                          onClickErrorCloseIcon('email')
                        }
                      />
                    </Box>
                  </Box>
                </Form>

                <Box className={classes.activateBtnWrapper}>
                  <CuminButton
                    variant="contained"
                    textColor={{
                      xs: 'whitePepper.main',
                      md: 'garamMasala.main',
                    }}
                    onClick={handleSignIn}
                    className={classes.activateBtn}
                  >
                    {sendActivationLinkLoading
                      ? 'Loading...'
                      : 'Send me an Activation Link'}
                  </CuminButton>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ActivateAccount;
